import React from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useOrg } from "../context/OrgContext";
import { useTerminology } from "../context/TerminologyContext";
import PinModal from "../components/PinModal";
import PageHeader from "../components/PageHeader";
import LocalPdfPreview from "../components/LocalPdfPreview";
import {
  AlertModal,
  ChecklistHeaderTable,
  DateRangePicker,
  LotSelectionTable,
  ModalShell,
  Pager,
  ReqLabel,
  usePagination,
  isChecklistMonthLabel,
  normalizeChecklistMonthForInput,
  normalizeChecklistHeaderLabel,
} from "../components/ui-helpers";
import { formatDateTime, parseDateLike } from "../utils/datetime";

const cleanValue = (value) => String(value ?? "").trim();

const firstValue = (source, keys) => {
  for (const key of keys) {
    const value = cleanValue(source?.[key]);
    if (value) return value;
  }
  return "";
};
const identityKeysFor = (row) => {
  const keys = [];
  const lot = firstValue(row, ["lot_no", "lot", "batch_no"]);
  const mid = cleanValue(row?.material_id);
  const sku = cleanValue(row?.sku);
  const vendor = cleanValue(row?.vendor);
  if (mid && lot) keys.push(`mid:${mid}||${lot}`);
  if (sku && lot) keys.push(`sku:${sku}||${lot}`);
  if (vendor && lot) keys.push(`vendor:${vendor}||${lot}`);
  if (lot) keys.push(`lot:${lot}`);
  return keys;
};
const primaryIdentityKey = (row) =>
  identityKeysFor(row)[0] ||
  cleanValue(row?.id) ||
  `${cleanValue(row?.sku)}||${firstValue(row, ["lot_no", "lot", "batch_no"])}`;
const sortByTimeDesc = (rows) =>
  [...(rows || [])].sort((a, b) => {
    const ad = parseDateLike(a.sortTime || a.time);
    const bd = parseDateLike(b.sortTime || b.time);
    if (ad && bd) return bd.getTime() - ad.getTime();
    if (bd) return 1;
    if (ad) return -1;
    return String(b.time || "").localeCompare(String(a.time || ""));
  });

const MATERIAL_WORKFLOW_AUDIT_ACTIONS = new Set(["MaterialSentBack", "MaterialWithdrawn"]);

const NOTIFICATION_SEARCH_FIELDS = [
  "vendor",
  "material",
  "material_name",
  "sku",
  "lot",
  "lot_no",
  "batch_no",
  "user",
  "uid",
  "actor",
  "actor_uid",
  "created_by",
  "title",
  "message",
  "comments",
  "comment",
  "action",
  "last_error",
  "reason",
];

const notificationMatchesSearch = (row, query) => {
  const q = cleanValue(query).toLowerCase();
  if (!q) return true;
  return NOTIFICATION_SEARCH_FIELDS.some((field) =>
    cleanValue(row?.[field]).toLowerCase().includes(q),
  );
};

const filterNotificationRows = (rows, query) =>
  (rows || []).filter((row) => notificationMatchesSearch(row, query));

const LOCKED_CHECKLIST_LABELS = [
  "Product Name",
  "Pack Size",
  "Batch No",
  "Batch No.",
];
const applyLockedChecklistValues = (
  template,
  currentValues,
  { material, sku, lot },
) => {
  const next = { ...(currentValues || {}) };
  (template?.header?.pairs || []).flat().forEach((label) => {
    const key = normalizeChecklistHeaderLabel(label);
    if (key === "productname") next[label] = material || "";
    if (key === "packsize") next[label] = sku || "";
    if (key === "batchno") next[label] = lot || "";
  });
  return next;
};
const canonicalChecklistCountKey = (label) => {
  const key = normalizeChecklistHeaderLabel(label);
  if (["y", "yes", "entery", "enteryes"].includes(key)) return "Y";
  if (["n", "no", "entern", "enterno"].includes(key)) return "N";
  if (["na", "n/a", "enterna", "entern/a"].includes(key)) return "NA";
  return "";
};

const readChecklistCount = (counts, header, code) => {
  const wanted = String(code || "").toUpperCase();
  for (const [key, value] of Object.entries(counts || {})) {
    if (
      canonicalChecklistCountKey(key) === wanted &&
      value != null &&
      String(value).trim() !== ""
    )
      return value;
  }
  for (const [key, value] of Object.entries(header || {})) {
    if (
      canonicalChecklistCountKey(key) === wanted &&
      value != null &&
      String(value).trim() !== ""
    )
      return value;
  }
  return undefined;
};

const mergePrefillIntoTemplateLabels = (template, header, counts) => {
  const sourceHeader = header || {};
  const normalizedCounts = {
    Y: readChecklistCount(counts, sourceHeader, "Y"),
    N: readChecklistCount(counts, sourceHeader, "N"),
    NA: readChecklistCount(counts, sourceHeader, "NA"),
  };
  const next = Object.fromEntries(
    Object.entries(sourceHeader).map(([label, value]) => [
      label,
      isChecklistMonthLabel(label)
        ? normalizeChecklistMonthForInput(value)
        : value,
    ]),
  );
  (template?.header?.pairs || []).flat().forEach((label) => {
    const code = canonicalChecklistCountKey(label);
    if (code && normalizedCounts[code] != null)
      next[label] = normalizedCounts[code];
    if (isChecklistMonthLabel(label)) {
      const labelKey = normalizeChecklistHeaderLabel(label);
      const sourceEntry = Object.entries(sourceHeader).find(([sourceLabel]) => {
        const sourceKey = normalizeChecklistHeaderLabel(sourceLabel);
        if (labelKey === "mfgdate") return sourceKey === "mfgdate";
        return ["expdateusebefore", "expirydate"].includes(labelKey)
          && ["expdateusebefore", "expirydate"].includes(sourceKey);
      });
      if (sourceEntry) next[label] = normalizeChecklistMonthForInput(sourceEntry[1]);
    }
  });
  return next;
};
const shallowSame = (a, b) => {
  const ak = Object.keys(a || {});
  const bk = Object.keys(b || {});
  if (ak.length !== bk.length) return false;
  return ak.every((key) => String(a?.[key] ?? "") === String(b?.[key] ?? ""));
};

function NotificationTable({
  rows,
  columns,
  emptyText,
  onRowClick,
  selectedRowId = "",
  getRowId,
}) {
  const pager = usePagination(rows, 10);
  return (
    <div className="table--card notifications-tab-card">
      <div className="table-scroll">
        <table className="table table--dense table--fixed">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pager.pageData.map((row, index) => {
              const rowId = getRowId
                ? getRowId(row, index)
                : row.id || `${row.sku || "row"}-${row.lot || index}`;
              const selected =
                selectedRowId && String(rowId) === String(selectedRowId);
              return (
                <tr
                  key={rowId}
                  className={selected ? "is-selected" : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={onRowClick ? { cursor: "pointer" } : undefined}
                >
                  {columns.map((column) => (
                    <td key={column.key}>
                      {column.render ? column.render(row) : row[column.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>{emptyText}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <Pager {...pager} />
    </div>
  );
}

export default function NotificationsPage() {
  const { user } = useAuth();
  const { current: currentOrg } = useOrg();
  const { t, tp } = useTerminology();
  const navigate = useNavigate();
  const uid = user?.uid || "";
  const currentRole = user?.currentRole || "";
  const isVendor = currentRole === "Vendor";
  const canViewMaterialWorkflowAudit = currentRole === "Admin" || currentRole === "Manager";
  const [activeTab, setActiveTab] = React.useState("inbox");
  const [items, setItems] = React.useState([]);
  const [rejected, setRejected] = React.useState([]);
  const [sentBackRows, setSentBackRows] = React.useState([]);
  const [invalidatedLegacy, setInvalidatedLegacy] = React.useState([]);
  const [invalidatedCurrent, setInvalidatedCurrent] = React.useState([]);
  const [heldLots, setHeldLots] = React.useState([]);
  const [heldSelected, setHeldSelected] = React.useState(() => new Set());
  const [pushBusy, setPushBusy] = React.useState(false);
  const [banner, setBanner] = React.useState(null);
  const [reupload, setReupload] = React.useState({
    lot: "",
    sku: "",
    material_id: "",
    material: "",
    file: null,
  });
  const [selectedInvalidatedKey, setSelectedInvalidatedKey] =
    React.useState("");
  const [prefillError, setPrefillError] = React.useState("");
  const [checkPdf, setCheckPdf] = React.useState(null);
  const [template, setTemplate] = React.useState(null);
  const [headerVals, setHeaderVals] = React.useState({});
  const [qAns, setQAns] = React.useState("N/A");
  const [qDev, setQDev] = React.useState("");
  const [qOpen, setQOpen] = React.useState(false);
  const [pinOpen, setPinOpen] = React.useState(false);
  const [pendingAction, setPendingAction] = React.useState(null);
  const [alert, setAlert] = React.useState({ open: false, msg: "", title: "" });
  const [dateDraft, setDateDraft] = React.useState({
    date_from: "",
    date_to: "",
  });
  const [dateRange, setDateRange] = React.useState({
    date_from: "",
    date_to: "",
  });
  const [notificationSearch, setNotificationSearch] = React.useState("");
  const [invalidatedTableOpen, setInvalidatedTableOpen] = React.useState(true);
  const coaFileRef = React.useRef(null);
  const checklistFileRef = React.useRef(null);

  const showAlert = (msg, title = "Notice") =>
    setAlert({ open: true, msg, title });
  const checklistLabels = React.useMemo(
    () =>
      (template?.header?.pairs || []).flatMap((pair) =>
        pair.filter((label) => String(label || "").trim() !== ""),
      ),
    [template],
  );
  const checklistEnabled =
    !!template &&
    template.enabled !== false &&
    (checklistLabels.length > 0 || !!template?.question?.text);

  React.useEffect(() => {
    if (activeTab === "sent-back" && !canViewMaterialWorkflowAudit) {
      setActiveTab("inbox");
    }
  }, [activeTab, canViewMaterialWorkflowAudit]);

  const reuploadResolutionMessage =
    reupload.material && reupload.sku && !reupload.material_id
      ? "No material ID is available for this invalidated lot yet; legacy re-upload will be attempted."
      : "";

  const isIntField = (label) => {
    const key = normalizeChecklistHeaderLabel(label);
    return ["enteryes", "enterno", "enterna", "y", "n", "na"].includes(key);
  };
  const isFloatField = (label) => {
    const key = String(label || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
      .trim();
    return key === "finalyield" || key === "mrp" || key === "mrprs";
  };
  const normalizeDecimal = (value) => {
    const cleaned = String(value ?? "")
      .replace(/[^\d.]/g, "")
      .replace(/(\..*)\./g, "$1");
    const match = cleaned.match(/^\d*(?:\.\d{0,3})?/);
    return match ? match[0] : "";
  };

  const reloadInbox = React.useCallback(() => {
    if (!uid) return;
    api
      .get("/notifications/inbox", {
        params: {
          uid,
          date_from: dateRange.date_from || undefined,
          date_to: dateRange.date_to || undefined,
        },
      })
      .then((response) => setItems(response.data.items || []))
      .catch(() => setItems([]));
  }, [uid, dateRange]);

  const loadTables = React.useCallback(async () => {
    try {
      const dateParams = {
        date_from: dateRange.date_from || undefined,
        date_to: dateRange.date_to || undefined,
      };
      const [rej, inv, sentBack, status] = await Promise.all([
        api.get("/audit/list", {
          params: {
            q: "Rejected",
            vendor: isVendor ? uid : undefined,
            ...dateParams,
          },
        }),
        api.get("/audit/list", {
          params: {
            q: "Invalidated",
            vendor: isVendor ? uid : undefined,
            ...dateParams,
          },
        }),
        canViewMaterialWorkflowAudit
          ? api.get("/audit/list", {
              params: {
                q: "Material",
                ...dateParams,
              },
            })
          : Promise.resolve({ data: { rows: [] } }),
        api.get("/status_table", {
          params: {
            currentOrg,
            vendor: isVendor ? uid : undefined,
            ...dateParams,
          },
        }),
      ]);

      const statusRows = status.data.rows || [];
      const statusInvalidatedRows = statusRows.filter(
        (row) => String(row.status || "").toLowerCase() === "invalidated",
      );
      const statusByIdentity = new Map();
      statusRows.forEach((row) => {
        identityKeysFor(row).forEach((key) => {
          if (!statusByIdentity.has(key)) statusByIdentity.set(key, row);
        });
      });

      const resolveStatusRow = (row) => {
        for (const key of identityKeysFor(row)) {
          if (statusByIdentity.has(key)) return statusByIdentity.get(key);
        }
        return null;
      };

      const normalizeAuditRow = (row, index, prefix) => {
        const statusRow = resolveStatusRow(row) || {};
        const lot =
          firstValue(row, ["lot_no", "lot", "batch_no"]) ||
          firstValue(statusRow, ["lot_no", "lot"]);
        const time =
          firstValue(row, ["time", "created_at", "timestamp"]) ||
          firstValue(statusRow, ["last_action_time", "upload_time"]);
        return {
          id: `${prefix}-${index}-${time || lot || "legacy"}`,
          user:
            firstValue(row, ["user", "actor", "actor_uid", "created_by"]) ||
            "—",
          vendor:
            firstValue(row, ["vendor"]) || firstValue(statusRow, ["vendor"]),
          lot,
          sku: firstValue(row, ["sku"]) || firstValue(statusRow, ["sku"]),
          material_id:
            firstValue(row, ["material_id", "materialId"]) ||
            firstValue(statusRow, ["material_id", "materialId"]),
          material:
            firstValue(row, ["material", "material_name"]) ||
            firstValue(statusRow, ["material", "material_name"]),
          action: firstValue(row, ["action"]),
          comments: firstValue(row, ["comments", "comment"]),
          time: time || "",
          sortTime: parseDateLike(time),
        };
      };

      const rejectedRows = (rej.data.rows || [])
        .filter((row) => String(row.action || "").toLowerCase() === "rejected")
        .map((row, index) => normalizeAuditRow(row, index, "rej"));
      const invalidatedRows = (inv.data.rows || [])
        .filter(
          (row) => String(row.action || "").toLowerCase() === "invalidated",
        )
        .map((row, index) => normalizeAuditRow(row, index, "inv"));
      const materialWorkflowRows = (sentBack.data.rows || [])
        .filter((row) => MATERIAL_WORKFLOW_AUDIT_ACTIONS.has(String(row.action || "")))
        .map((row, index) => normalizeAuditRow(row, index, "mat"));

      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);

      // `statusInvalidatedRows` is already the backend's lineage-collapsed
      // *current* state. Audit can legitimately contain more than one
      // Invalidated event for the same lot as it moves through replacement
      // material versions. Keep only the latest matching Invalidated audit
      // event for each current status row; older invalidations remain history.
      const currentInvalidatedAuditIds = new Set();
      const currentInvalidatedRows = statusInvalidatedRows.map(
        (statusRow, index) => {
          const statusLot = firstValue(statusRow, ["lot_no", "lot", "batch_no"]);
          const statusMaterialId = cleanValue(statusRow?.material_id);
          const statusVendor = cleanValue(statusRow?.vendor);

          const sameLotAndVendor = (auditRow) => {
            const auditLot = firstValue(auditRow, ["lot_no", "lot", "batch_no"]);
            const auditVendor = cleanValue(auditRow?.vendor);
            if (!statusLot || auditLot !== statusLot) return false;
            if (statusVendor && auditVendor && auditVendor !== statusVendor) return false;
            return true;
          };

          let candidates = invalidatedRows.filter((auditRow) => {
            if (!sameLotAndVendor(auditRow)) return false;
            const auditMaterialId = cleanValue(auditRow?.material_id);
            return Boolean(
              statusMaterialId &&
                auditMaterialId &&
                auditMaterialId === statusMaterialId,
            );
          });

          // Legacy audit rows may not carry material_id. Fall back to the
          // existing identity matching only when an exact material-version
          // match is unavailable.
          if (!candidates.length) {
            const statusKeys = new Set(identityKeysFor(statusRow));
            candidates = invalidatedRows.filter(
              (auditRow) =>
                sameLotAndVendor(auditRow) &&
                identityKeysFor(auditRow).some((key) => statusKeys.has(key)),
            );
          }

          const latestAudit = sortByTimeDesc(candidates)[0] || null;
          if (latestAudit?.id) currentInvalidatedAuditIds.add(latestAudit.id);

          const time =
            latestAudit?.time ||
            firstValue(statusRow, ["last_action_time", "upload_time"]);
          return {
            ...(latestAudit || {}),
            id:
              latestAudit?.id ||
              `inv-current-${index}-${time || statusLot || "current"}`,
            vendor: firstValue(statusRow, ["vendor"]) || latestAudit?.vendor || "",
            material:
              firstValue(statusRow, ["material", "material_name"]) ||
              latestAudit?.material ||
              "",
            sku: firstValue(statusRow, ["sku"]) || latestAudit?.sku || "",
            material_id:
              firstValue(statusRow, ["material_id", "materialId"]) ||
              latestAudit?.material_id ||
              "",
            lot: statusLot || latestAudit?.lot || "",
            time: time || "",
            sortTime: parseDateLike(time),
          };
        },
      );

      setRejected(sortByTimeDesc(rejectedRows));
      setSentBackRows(sortByTimeDesc(materialWorkflowRows));
      setInvalidatedCurrent(sortByTimeDesc(currentInvalidatedRows));
      setInvalidatedLegacy(
        sortByTimeDesc(
          invalidatedRows.filter((row) => {
            if (currentInvalidatedAuditIds.has(row.id)) return false;
            const parsed = parseDateLike(row.time);
            return parsed ? parsed >= cutoff : true;
          }),
        ),
      );
    } catch {
      setRejected([]);
      setSentBackRows([]);
      setInvalidatedLegacy([]);
      setInvalidatedCurrent([]);
    }
  }, [currentOrg, isVendor, uid, dateRange, canViewMaterialWorkflowAudit]);

  const loadHeldLots = React.useCallback(async () => {
    if (isVendor) {
      setHeldLots([]);
      return;
    }
    try {
      const r = await api.get("/usage/held_lots");
      setHeldLots(r.data?.items || []);
    } catch {
      setHeldLots([]);
    }
  }, [isVendor, currentOrg]);

  React.useEffect(() => {
    reloadInbox();
    loadTables().catch(() => {});
    loadHeldLots().catch(() => {});
  }, [reloadInbox, loadTables, loadHeldLots]);

  React.useEffect(() => {
    setTemplate(null);
    setHeaderVals({});
    setQAns("N/A");
    setQDev("");
    setCheckPdf(null);
    if (checklistFileRef.current) checklistFileRef.current.value = "";
    if (!reupload.sku) return;
    api
      .get("/checklists/template", {
        params: {
          vendor: uid,
          sku: reupload.sku,
          for_invalidated_reupload: true,
          ...(reupload.material_id
            ? { material_id: reupload.material_id }
            : {}),
        },
      })
      .then((response) => setTemplate(response.data || null))
      .catch(() => setTemplate(null));
  }, [reupload.sku, reupload.material_id, uid, currentOrg]);

  React.useEffect(() => {
    if (!template) return;
    setHeaderVals((current) => {
      const next = applyLockedChecklistValues(template, current, {
        material: reupload.material,
        sku: reupload.sku,
        lot: reupload.lot,
      });
      return shallowSame(current, next) ? current : next;
    });
  }, [template, reupload.material, reupload.sku, reupload.lot]);

  React.useEffect(() => {
    if (!uid || !reupload.sku || !reupload.lot) return undefined;
    let cancelled = false;
    api
      .get("/vendor/invalidated_checklist_prefill", {
        params: {
          vendor: uid,
          sku: reupload.sku,
          lot_no: reupload.lot,
          ...(reupload.material_id
            ? { material_id: reupload.material_id }
            : {}),
        },
      })
      .then((response) => {
        if (cancelled) return;
        const data = response.data || {};
        const mergedHeader = mergePrefillIntoTemplateLabels(
          template,
          data.header || {},
          data.counts || {},
        );
        setHeaderVals((current) => {
          const next = applyLockedChecklistValues(
            template,
            { ...current, ...mergedHeader },
            {
              material: reupload.material,
              sku: reupload.sku,
              lot: reupload.lot,
            },
          );
          return shallowSame(current, next) ? current : next;
        });
        setQAns(data.answer ?? "N/A");
        setQDev(data.deviation_no ?? "");
        setPrefillError("");
        if (reupload.material_id) setInvalidatedTableOpen(false);
      })
      .catch(() => {
        setInvalidatedTableOpen(true);
        setPrefillError(
          "Could not load the previous checklist prefill. Locked fields are still filled from the selected lot; please review the checklist fields before uploading.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [
    uid,
    reupload.sku,
    reupload.lot,
    reupload.material_id,
    reupload.material,
    template,
    currentOrg,
  ]);

  const resetReuploadState = React.useCallback(() => {
    setReupload({
      lot: "",
      sku: "",
      material_id: "",
      material: "",
      file: null,
    });
    setSelectedInvalidatedKey("");
    setPrefillError("");
    setCheckPdf(null);
    setHeaderVals({});
    setTemplate(null);
    setQAns("N/A");
    setQDev("");
    setQOpen(false);
    setInvalidatedTableOpen(true);
    if (coaFileRef.current) coaFileRef.current.value = "";
    if (checklistFileRef.current) checklistFileRef.current.value = "";
  }, []);

  const selectInvalidatedRow = React.useCallback(
    (row) => {
      if (!isVendor) return;
      const lot = firstValue(row, ["lot", "lot_no", "batch_no"]);
      const sku = cleanValue(row.sku);
      const material = firstValue(row, ["material", "material_name"]);
      const materialId = firstValue(row, ["material_id", "materialId"]);
      const key = primaryIdentityKey(row);
      setSelectedInvalidatedKey(key);
      setPrefillError("");
      setBanner(null);
      setReupload({ lot, sku, material_id: materialId, material, file: null });
      setCheckPdf(null);
      setHeaderVals({});
      setQAns("N/A");
      setQDev("");
      setQOpen(false);
      if (!materialId) setInvalidatedTableOpen(true);
      if (coaFileRef.current) coaFileRef.current.value = "";
      if (checklistFileRef.current) checklistFileRef.current.value = "";
    },
    [isVendor],
  );

  React.useEffect(() => {
    if (!isVendor || !selectedInvalidatedKey) return;
    const stillCurrent = invalidatedCurrent.some(
      (row) => primaryIdentityKey(row) === selectedInvalidatedKey,
    );
    if (!stillCurrent) resetReuploadState();
  }, [
    invalidatedCurrent,
    isVendor,
    selectedInvalidatedKey,
    resetReuploadState,
  ]);

  const validateChecklistRequired = () => {
    if (!checklistEnabled) return "";
    const missing = checklistLabels.filter(
      (label) => !String(headerVals[label] ?? "").trim(),
    );
    if (missing.length) return `Please fill: ${missing.join(", ")}`;
    if ((qAns || "").toUpperCase() === "Y" && !String(qDev || "").trim())
      return template?.question?.extra_label || "Deviation No. is required.";
    return "";
  };

  const performReupload = async () => {
    if (!reupload.lot || !reupload.sku || !reupload.file) {
      setBanner({
        kind: "warn",
        text: "Select an invalidated lot and attach the corrected COA PDF.",
      });
      return;
    }

    const err = validateChecklistRequired();
    if (err) {
      showAlert(err, "Missing fields");
      return;
    }

    const form = new FormData();
    form.append("vendor_name", uid);
    form.append("sku", reupload.sku);
    if (reupload.material_id) form.append("material_id", reupload.material_id);
    form.append("lot_no", reupload.lot);
    form.append("file", reupload.file);
    if (checklistEnabled) {
      if (checkPdf) form.append("checklist", checkPdf);
      if (Object.keys(headerVals).length)
        form.append("header_json", JSON.stringify(headerVals));
      if (template?.question?.text) {
        form.append("q_answer", qAns);
        if (qDev) form.append("q_deviation_no", qDev);
      }
    }

    try {
      await api.post("/vendor/reupload_invalidated", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBanner({
        kind: "success",
        text: "Re-upload queued. The form is ready for the next lot.",
      });
      resetReuploadState();
      await loadTables();
    } catch (err2) {
      setBanner({
        kind: "error",
        text:
          err2?.response?.data?.error ||
          "Could not re-upload. Make sure the lot is currently invalidated.",
      });
    }
  };

  const openQuestionThenPin = () => {
    if (!reupload.lot || !reupload.sku || !reupload.file) {
      setBanner({
        kind: "warn",
        text: "Select an invalidated lot and attach the corrected COA PDF.",
      });
      return;
    }
    const err = validateChecklistRequired();
    if (err) {
      showAlert(err, "Missing fields");
      return;
    }
    if (checklistEnabled && template?.question?.text) {
      setQOpen(true);
      return;
    }
    setPendingAction(() => performReupload);
    setPinOpen(true);
  };

  const markReadAndOpen = async (item) => {
    try {
      const form = new FormData();
      form.append("uid", uid);
      form.append("ids", item.id);
      await api.post("/notifications/mark_read", form);
    } catch {
      // no-op
    }
    navigate(item.link || "/");
  };

  const commonColumns = [
    { key: "user", label: "User (by who)" },
    { key: "sku", label: "SKU", render: (row) => <span>{row.sku}</span> },
    { key: "lot", label: t("lot") },
    { key: "comments", label: "Comments" },
    { key: "time", label: "Time", render: (row) => formatDateTime(row.time) },
  ];

  const materialWorkflowColumns = [
    { key: "user", label: "Actor / User" },
    { key: "vendor", label: t("vendor") },
    { key: "material", label: t("material") },
    { key: "sku", label: "SKU" },
    { key: "action", label: "Action" },
    { key: "comments", label: "Comments" },
    { key: "time", label: "Time", render: (row) => formatDateTime(row.time) },
  ];

  const openMaterialWorkflowRow = () => {
    if (currentRole === "Admin") navigate("/admin/vendors");
    else if (currentRole === "Manager") navigate("/manager/review-materials");
  };

  const invalidatedCurrentColumns = React.useMemo(
    () => [
      { key: "vendor", label: t("vendor") },
      { key: "material", label: t("material") },
      { key: "sku", label: "SKU" },
      { key: "lot", label: t("lot") },
      {
        key: "time",
        label: "Time",
        value: (row) => row.sortTime?.getTime?.() || row.time || "",
        render: (row) => formatDateTime(row.time),
      },
      { key: "comments", label: "Comments" },
    ],
    [t],
  );

  const invalidatedCurrentSortOptions = React.useMemo(
    () => [
      { key: "vendor", label: t("vendor") },
      { key: "material", label: t("material") },
      { key: "sku", label: "SKU" },
      { key: "lot", label: t("lot") },
      {
        key: "time",
        label: "Time / Last Action",
        value: (row) => row.sortTime?.getTime?.() || row.time || "",
      },
    ],
    [t],
  );

  const searchedItems = React.useMemo(
    () => filterNotificationRows(items, notificationSearch),
    [items, notificationSearch],
  );
  const searchedRejected = React.useMemo(
    () => filterNotificationRows(rejected, notificationSearch),
    [rejected, notificationSearch],
  );
  const searchedSentBackRows = React.useMemo(
    () => filterNotificationRows(sentBackRows, notificationSearch),
    [sentBackRows, notificationSearch],
  );
  const searchedInvalidatedCurrent = React.useMemo(
    () => filterNotificationRows(invalidatedCurrent, notificationSearch),
    [invalidatedCurrent, notificationSearch],
  );
  const searchedInvalidatedLegacy = React.useMemo(
    () => filterNotificationRows(invalidatedLegacy, notificationSearch),
    [invalidatedLegacy, notificationSearch],
  );
  const searchedHeldLots = React.useMemo(
    () => filterNotificationRows(heldLots, notificationSearch),
    [heldLots, notificationSearch],
  );

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Workflow"
        icon="notifications"
        title="Notifications"
        subtitle={`Review inbox activity, rejected ${tp("lot").toLowerCase()}, and invalidated ${tp("lot").toLowerCase()}.`}
      />

      {banner ? (
        <div
          className={`banner ${banner.kind === "error" ? "error" : banner.kind === "success" ? "success" : "warn"}`}
        >
          {banner.text}
        </div>
      ) : null}

      <div className="section">
        <div className="filters-bar notifications-toolbar">
          <label className="field-grow">
            <span>Search</span>
            <input
              placeholder={`Search active tab by ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, SKU, ${t("lot").toLowerCase()}, user, title, or comments`}
              value={notificationSearch}
              onChange={(event) => setNotificationSearch(event.target.value)}
            />
          </label>
          <DateRangePicker
            value={dateDraft}
            onChange={setDateDraft}
            onApply={() => setDateRange({ ...dateDraft })}
            onClear={(empty) => setDateRange(empty)}
          />
        </div>
      </div>

      <div className="tabs notifications-page-tabs">
        <button
          className={activeTab === "inbox" ? "chip chip-on" : "chip"}
          type="button"
          onClick={() => setActiveTab("inbox")}
        >
          Inbox
        </button>
        <button
          className={activeTab === "rejected" ? "chip chip-on" : "chip"}
          type="button"
          onClick={() => setActiveTab("rejected")}
        >
          Rejected
        </button>
        {canViewMaterialWorkflowAudit ? (
          <button
            className={activeTab === "sent-back" ? "chip chip-on" : "chip"}
            type="button"
            onClick={() => setActiveTab("sent-back")}
          >
            Revert / Withdrawn
          </button>
        ) : null}
        <button
          className={
            activeTab === "invalidated-current" ? "chip chip-on" : "chip"
          }
          type="button"
          onClick={() => setActiveTab("invalidated-current")}
        >
          Invalidated (Current)
        </button>
        <button
          className={
            activeTab === "invalidated-legacy" ? "chip chip-on" : "chip"
          }
          type="button"
          onClick={() => setActiveTab("invalidated-legacy")}
        >
          Invalidated (Legacy)
        </button>
        {!isVendor ? (
          <button
            className={activeTab === "onhold" ? "chip chip-on" : "chip"}
            type="button"
            onClick={() => setActiveTab("onhold")}
          >
            On Hold{heldLots.length ? ` (${heldLots.length})` : ""}
          </button>
        ) : null}
      </div>

      {activeTab === "inbox" ? (
        <NotificationTable
          rows={searchedItems}
          emptyText="No notifications yet."
          onRowClick={markReadAndOpen}
          columns={[
            {
              key: "time",
              label: "When",
              render: (row) => formatDateTime(row.time),
            },
            { key: "title", label: "Title" },
            { key: "message", label: "Message" },
          ]}
        />
      ) : null}

      {activeTab === "rejected" ? (
        <NotificationTable
          rows={searchedRejected}
          columns={commonColumns}
          emptyText={`No rejected ${tp("lot").toLowerCase()} found.`}
        />
      ) : null}

      {activeTab === "sent-back" && canViewMaterialWorkflowAudit ? (
        <NotificationTable
          rows={searchedSentBackRows}
          columns={materialWorkflowColumns}
          emptyText={`No reverted or withdrawn ${tp("material").toLowerCase()} audit rows found.`}
          onRowClick={openMaterialWorkflowRow}
        />
      ) : null}

      {activeTab === "invalidated-current" ? (
        <div className="page-stack">
          <details
            className="selection-expander"
            open={invalidatedTableOpen}
            onToggle={(event) =>
              setInvalidatedTableOpen(event.currentTarget.open)
            }
          >
            <summary>Choose an invalidated {t("lot").toLowerCase()}</summary>
            <div className="helper-text" style={{ marginBottom: 10 }}>
              {isVendor
                ? "Select a currently invalidated row to load the re-upload form."
                : `Current invalidated ${tp("lot").toLowerCase()} are listed below for review.`}
            </div>
            <LotSelectionTable
              rows={searchedInvalidatedCurrent}
              columns={invalidatedCurrentColumns}
              sortOptions={invalidatedCurrentSortOptions}
              selectionMode={isVendor ? "single" : "none"}
              selected={isVendor ? selectedInvalidatedKey : ""}
              onRowClick={isVendor ? selectInvalidatedRow : undefined}
              getRowId={(row) => primaryIdentityKey(row)}
              searchPlaceholder={`Search ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, SKU, ${t("lot").toLowerCase()}, or comments`}
              hideSearch={true}
              emptyText={`No currently invalidated ${tp("lot").toLowerCase()} found.`}
              pageSize={10}
              className="notifications-invalidated-current-table"
            />
          </details>

          {isVendor ? (
            <div className="section notify-template-card">
              <div className="page-header-row">
                <div>
                  <h3>Re-upload invalidated {t("lot").toLowerCase()}</h3>
                  <div className="helper-text">
                    Select an invalidated row above, then submit the corrected
                    COA package.
                  </div>
                </div>
              </div>

              {!reupload.lot ? (
                <div className="helper-text notify-reupload-empty">
                  Select an invalidated {t("lot").toLowerCase()} above to start
                  re-upload.
                </div>
              ) : (
                <>
                  <div className="selected-lot-summary notify-selected-lot">
                    <div>
                      <span>{t("material")}</span>
                      <strong>{reupload.material || "—"}</strong>
                    </div>
                    <div>
                      <span>SKU</span>
                      <strong>{reupload.sku || "—"}</strong>
                    </div>
                    <div>
                      <span>{t("lot")}</span>
                      <strong>{reupload.lot || "—"}</strong>
                    </div>
                  </div>
                  {reuploadResolutionMessage ? (
                    <div className="helper-text error-text">
                      {reuploadResolutionMessage}
                    </div>
                  ) : null}
                  {prefillError ? (
                    <div className="helper-text error-text">{prefillError}</div>
                  ) : null}
                </>
              )}

              <div className="notifications-reupload-grid">
                <div className="field">
                  <ReqLabel>COA PDF</ReqLabel>
                  <div className="file-preview-row">
                    <input
                      ref={coaFileRef}
                      className="premium-file-input"
                      type="file"
                      accept="application/pdf"
                      disabled={!reupload.lot}
                      onChange={(e) =>
                        setReupload((current) => ({
                          ...current,
                          file: e.target.files?.[0] || null,
                        }))
                      }
                    />
                    <LocalPdfPreview file={reupload.file} title="Corrected COA PDF" />
                  </div>
                </div>

                {checklistEnabled ? (
                  <>
                    {checklistLabels.length ? (
                      <div className="field span-3 checklist-header-shell">
                        <label>Checklist header</label>
                        <ChecklistHeaderTable
                          template={template}
                          values={headerVals}
                          onChange={(label, value) => {
                            const key = normalizeChecklistHeaderLabel(label);
                            if (
                              ["productname", "packsize", "batchno"].includes(
                                key,
                              )
                            )
                              return;
                            setHeaderVals((prev) => ({
                              ...prev,
                              [label]: value,
                            }));
                          }}
                          isMonthField={isChecklistMonthLabel}
                          isIntField={isIntField}
                          isFloatField={isFloatField}
                          normalizeDecimal={normalizeDecimal}
                          lockedLabels={LOCKED_CHECKLIST_LABELS}
                        />
                      </div>
                    ) : null}
                    <div className="field span-2 checklist-upload-field">
                      <label>Checklist PDF (filled)</label>
                      <div className="file-preview-row">
                        <input
                          ref={checklistFileRef}
                          className="premium-file-input"
                          type="file"
                          accept="application/pdf"
                          disabled={!reupload.lot}
                          onChange={(e) =>
                            setCheckPdf(e.target.files?.[0] || null)
                          }
                        />
                        <LocalPdfPreview file={checkPdf} title="Checklist PDF" />
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
              <div className="card-actions">
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={openQuestionThenPin}
                  disabled={!reupload.lot}
                >
                  Upload
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {activeTab === "invalidated-legacy" ? (
        <NotificationTable
          rows={searchedInvalidatedLegacy}
          columns={commonColumns}
          emptyText="No invalidated history found."
        />
      ) : null}

      {activeTab === "onhold" && !isVendor ? (
        <div className="section notify-onhold-card">
          <div className="page-header-row">
            <div>
              <h3>{tp("lot")} awaiting credits</h3>
              <div className="helper-text">
                {`These ${tp("lot").toLowerCase()} were uploaded but are waiting for available credits before processing. Once credits are available, select ${tp("lot").toLowerCase()} and push them into the ready queue.`}
              </div>
            </div>
            <div className="onhold-actions">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() =>
                  setHeldSelected(
                    new Set(searchedHeldLots.map((row) => row.id)),
                  )
                }
                disabled={searchedHeldLots.length === 0}
              >
                Select all
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setHeldSelected(new Set())}
                disabled={heldSelected.size === 0}
              >
                Clear
              </button>
              <button
                className="btn btn-primary"
                type="button"
                disabled={heldSelected.size === 0 || pushBusy}
                onClick={async () => {
                  if (heldSelected.size === 0) return;
                  setPushBusy(true);
                  try {
                    const ids = Array.from(heldSelected).join(",");
                    const form = new FormData();
                    form.set("queue_ids", ids);
                    form.set("actor", uid || "");
                    const res = await api.post("/usage/held_lots/push", form, {
                      headers: { "Content-Type": "multipart/form-data" },
                    });
                    const data = res.data || {};
                    if (data.pushed && !data.still_held) {
                      setBanner({
                        kind: "success",
                        text: `Pushed ${data.pushed} ${data.pushed === 1 ? t("lot").toLowerCase() : tp("lot").toLowerCase()} to the queue.`,
                      });
                    } else if (data.pushed && data.still_held) {
                      setBanner({
                        kind: "warn",
                        text: `Pushed ${data.pushed} ${tp("lot").toLowerCase()}; ${data.still_held} still on hold (insufficient credits).`,
                      });
                    } else if (data.still_held) {
                      setBanner({
                        kind: "warn",
                        text: `No ${tp("lot").toLowerCase()} pushed: ${data.still_held} remained on hold (insufficient credits).`,
                      });
                    } else {
                      setBanner({
                        kind: "warn",
                        text: `No ${tp("lot").toLowerCase()} were pushed.`,
                      });
                    }
                    setHeldSelected(new Set());
                    await loadHeldLots();
                  } catch (e) {
                    setBanner({
                      kind: "error",
                      text:
                        e?.response?.data?.error ||
                        `Could not push ${tp("lot").toLowerCase()} to the queue.`,
                    });
                  } finally {
                    setPushBusy(false);
                  }
                }}
              >
                Push to queue ({heldSelected.size})
              </button>
            </div>
          </div>
          <div className="table--card">
            <div className="table-scroll">
              <table className="table table--dense table--fixed">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={
                          searchedHeldLots.length > 0 &&
                          searchedHeldLots.every((row) =>
                            heldSelected.has(row.id),
                          )
                        }
                        onChange={(e) => {
                          if (e.target.checked)
                            setHeldSelected(
                              new Set(searchedHeldLots.map((row) => row.id)),
                            );
                          else setHeldSelected(new Set());
                        }}
                      />
                    </th>
                    <th>{t("vendor")}</th>
                    <th>{t("material")}</th>
                    <th>SKU</th>
                    <th>{t("lot")}</th>
                    <th>On hold since</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {searchedHeldLots.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={heldSelected.has(row.id)}
                          onChange={(e) => {
                            setHeldSelected((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(row.id);
                              else next.delete(row.id);
                              return next;
                            });
                          }}
                        />
                      </td>
                      <td>{row.vendor || "—"}</td>
                      <td>{row.material || "—"}</td>
                      <td>{row.sku || "—"}</td>
                      <td>{row.lot_no || "—"}</td>
                      <td>{formatDateTime(row.hold_since)}</td>
                      <td>{row.last_error || "Insufficient credits"}</td>
                    </tr>
                  ))}
                  {searchedHeldLots.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        {notificationSearch.trim()
                          ? `No on-hold ${tp("lot").toLowerCase()} match your search.`
                          : `No ${tp("lot").toLowerCase()} are currently on hold.`}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      <ModalShell
        open={qOpen}
        onClose={() => setQOpen(false)}
        title="Checklist confirmation"
        subtitle={`Confirm the checklist question before the invalidated ${t("lot").toLowerCase()} is re-uploaded.`}
        size="sm"
        actions={
          <>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setQOpen(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => {
                setQOpen(false);
                setPendingAction(() => performReupload);
                setPinOpen(true);
              }}
            >
              Confirm &amp; Submit
            </button>
          </>
        }
      >
        <div className="page-stack">
          <div>
            <strong>Question:</strong> {template?.question?.text}{" "}
            {template?.question?.code ? (
              <small>({template.question.code})</small>
            ) : null}
          </div>
          <div className="hstack" style={{ gap: 12, flexWrap: "wrap" }}>
            <div className="field" style={{ minWidth: 180 }}>
              <ReqLabel>Answer</ReqLabel>
              <select value={qAns} onChange={(e) => setQAns(e.target.value)}>
                <option>N/A</option>
                <option>Y</option>
                <option>N</option>
              </select>
            </div>
            {qAns === "Y" ? (
              <div className="field" style={{ flex: "1 1 220px" }}>
                <ReqLabel>
                  {template?.question?.extra_label || "Deviation No."}
                </ReqLabel>
                <input value={qDev} onChange={(e) => setQDev(e.target.value)} />
              </div>
            ) : null}
          </div>
        </div>
      </ModalShell>

      <PinModal
        uid={user?.uid}
        open={pinOpen}
        onClose={() => setPinOpen(false)}
        onVerified={async () => {
          setPinOpen(false);
          if (pendingAction) await pendingAction();
        }}
      />

      <AlertModal
        open={alert.open}
        title={alert.title}
        message={alert.msg}
        onClose={() => setAlert({ open: false, msg: "", title: "" })}
      />
    </div>
  );
}
